/// End-of-level overlay. Shown when the session reports a finished
/// state. Primary action depends on the outcome:
///
///   - Success ⟹ "Level select" (return to the home screen — they
///                completed it, no reason to drop back into the
///                same brief). A secondary "Replay" button lets
///                them push for a higher score.
///   - Failure ⟹ "Try again" (re-mount the GameView). A secondary
///                "Level select" lets them bail out.

import type { ScoreCategory } from '../game/GameSession'
import {
  skillRating,
  skillRatingDescription,
  type SkillRating,
} from '../game/skillRating'
import type { ScoreState } from '../state/store'

/// Order rows are rendered in. Empty categories drop out below.
const ROW_ORDER: readonly ScoreCategory[] = [
  'landings',
  'departures',
  'transitions',
  'bonuses',
  'conflicts',
  'violations',
] as const

const ROW_LABEL: Record<ScoreCategory, string> = {
  landings: 'Landings',
  transitions: 'Transitions',
  departures: 'Departures',
  bonuses: 'Bonuses',
  conflicts: 'Conflicts',
  violations: 'Violations',
}

export function EndScreen(props: {
  success: boolean
  score: ScoreState
  /// Live-shift summary (positioned Handoff levels); null on normal levels.
  live?: { worked: number; goal: number; handoffs: number; unhandledExits: number } | null
  onRestart: () => void
  onHome: () => void
}) {
  const { success, score, live, onRestart, onHome } = props
  const handled = score.successCount + score.failCount
  const successPct = handled > 0 ? Math.round((score.successCount / handled) * 100) : 0
  const visibleRows = ROW_ORDER.filter((k) => score.breakdown[k].count > 0)
  const rating = skillRating(handled, score.separationInfringements)
  return (
    <div className="pointer-events-auto absolute inset-0 z-30 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="max-w-sm rounded-xl bg-slate-900 p-8 text-center text-slate-100 shadow-2xl ring-1 ring-white/10">
        <h2 className="text-2xl font-bold tracking-wide">
          {live
            ? success
              ? 'Shift Complete'
              : 'Shift Ended'
            : success
              ? 'Level Complete'
              : 'Level Failed'}
        </h2>
        <p className="mt-2 text-sm text-slate-300">
          {live
            ? success
              ? 'Goal reached — nicely worked.'
              : 'Shift ended.'
            : success
              ? 'Nicely flown.'
              : 'Better luck next time.'}
        </p>
        {live && (
          <div className="mt-4 rounded-lg bg-slate-800/60 px-4 py-3 ring-1 ring-white/10">
            <div className="text-3xl font-bold text-emerald-300">
              {live.worked}
              <span className="text-lg text-slate-400">/{live.goal}</span>
            </div>
            <div className="text-[11px] uppercase tracking-wider text-slate-400">
              Aircraft worked
            </div>
            <div className="mt-2 flex justify-center gap-4 text-xs text-slate-300">
              <span>
                Handoffs <span className="font-mono text-slate-100">{live.handoffs}</span>
              </span>
              <span>
                Unhandled exits{' '}
                <span className="font-mono text-slate-100">{live.unhandledExits}</span>
              </span>
            </div>
          </div>
        )}
        <dl className="mt-5 grid grid-cols-[1fr_auto_auto] gap-x-3 gap-y-1.5 text-left text-sm">
          {visibleRows.map((k) => {
            const total = score.breakdown[k]
            return (
              <Row
                key={k}
                label={ROW_LABEL[k]}
                count={total.count}
                points={total.points}
              />
            )
          })}
          {visibleRows.length > 0 && (
            <div className="col-span-3 my-1 border-t border-white/10" />
          )}
          <dt className="text-slate-400">Total</dt>
          <dd className="text-right font-mono text-slate-100">
            {handled > 0 ? `${score.successCount}/${handled}` : '—'}
            {handled > 0 && (
              <span className="ml-1 text-xs text-slate-500">{successPct}%</span>
            )}
          </dd>
          <dd className="text-right font-mono font-semibold text-slate-100">
            {score.total.toLocaleString()}
          </dd>
        </dl>
        <SkillRatingRow
          rating={rating}
          infringements={score.separationInfringements}
        />
        <div className="mt-6 flex justify-center gap-2">
          {success ? (
            <>
              <button
                onClick={onRestart}
                className="rounded-md px-4 py-2 text-sm font-semibold text-slate-300 hover:bg-white/5 hover:text-white"
              >
                Replay
              </button>
              <button
                onClick={onHome}
                className="rounded-md bg-emerald-600 px-5 py-2 text-sm font-semibold text-white shadow hover:bg-emerald-500"
              >
                Level select
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onHome}
                className="rounded-md px-4 py-2 text-sm font-semibold text-slate-300 hover:bg-white/5 hover:text-white"
              >
                Level select
              </button>
              <button
                onClick={onRestart}
                className="rounded-md bg-emerald-600 px-5 py-2 text-sm font-semibold text-white shadow hover:bg-emerald-500"
              >
                Try again
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

/// Skill-rating chip + infringement count, rendered between the
/// scorecard total and the action buttons. Always visible so a
/// player who flew clean sees their A+ rather than no row at all.
function SkillRatingRow(props: { rating: SkillRating; infringements: number }) {
  return (
    <div className="mt-4 flex items-center justify-between rounded-md bg-slate-800/60 px-3 py-2 text-sm">
      <div className="text-left">
        <div className="text-[11px] uppercase tracking-wider text-slate-400">
          Skill rating
        </div>
        <div className="text-xs text-slate-300">
          {skillRatingDescription(props.rating)}
          {props.infringements > 0 && (
            <span className="ml-1.5 text-slate-500">
              · {props.infringements} infringement
              {props.infringements === 1 ? '' : 's'}
            </span>
          )}
        </div>
      </div>
      <div
        className={
          'rounded-md px-3 py-1 text-base font-bold tracking-wide ' +
          ratingStyle(props.rating)
        }
      >
        {props.rating}
      </div>
    </div>
  )
}

function ratingStyle(rating: SkillRating): string {
  switch (rating) {
    case 'A+':
      return 'bg-emerald-500/30 text-emerald-200 ring-1 ring-emerald-400/40'
    case 'A':
      return 'bg-emerald-600/30 text-emerald-200 ring-1 ring-emerald-500/40'
    case 'B':
      return 'bg-sky-600/30 text-sky-200 ring-1 ring-sky-500/40'
    case 'C':
      return 'bg-amber-600/30 text-amber-200 ring-1 ring-amber-500/40'
    case 'D':
      return 'bg-orange-600/30 text-orange-200 ring-1 ring-orange-500/40'
    case 'F':
      return 'bg-rose-700/40 text-rose-200 ring-1 ring-rose-500/40'
  }
}

function Row(props: { label: string; count: number; points: number }) {
  const positive = props.points >= 0
  // Format with a leading sign so negatives are unambiguous in the
  // total column (e.g. "-650" reads cleaner than "650").
  const fmt = `${positive ? '+' : ''}${props.points.toLocaleString()}`
  return (
    <>
      <dt className="text-slate-300">{props.label}</dt>
      <dd className="text-right font-mono text-slate-400">×{props.count}</dd>
      <dd
        className={
          'text-right font-mono ' + (positive ? 'text-emerald-300' : 'text-rose-300')
        }
      >
        {fmt}
      </dd>
    </>
  )
}
